// @ts-check
import { shallowEqualMemoize } from './utils/selector-utils'

/**
 * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
 * @returns {Record<EveryVitalType, VitalTypeDefinition>}
 */
export const getVitalTypesById = opts => ({
  bp: {
    name: 'Blood Pressure',
    chartName: 'BP',
    type: 'bp',
    includeInComparison: false,
    includeInAuto: true,
    unit: 'mmHg',
  },
  sbp: {
    name: 'Systolic Blood Pressure',
    chartName: 'SBP',
    startValue: 120,
    type: 'number',
    min: 0,
    max: 400,
    unit: 'mmHg',
    includeInComparison: true,
    includeInAuto: true,
    subItem: true,
    includeInManual: true,
  },
  dbp: {
    name: 'Diastolic Blood Pressure',
    chartName: 'DBP',
    startValue: 80,
    type: 'number',
    min: 0,
    max: 400,
    unit: 'mmHg',
    includeInComparison: true,
    includeInAuto: true,
    subItem: true,
    includeInManual: true,
  },
  map: {
    name: 'Mean Arterial Pressure',
    chartName: 'MAP',
    startValue: 90,
    type: 'number',
    min: 0,
    max: 400,
    unit: 'mmHg',
    includeInComparison: true,
    includeInAuto: true,
    subItem: true,
  },
  hr: {
    name: 'Heart Rate',
    chartName: 'HR',
    includeInComparison: true,
    includeInAuto: true,
    startValue: 80,
    type: 'number',
    min: 0,
    max: 500,
    unit: 'bpm',
    includeInManual: true,
  },
  spo2: {
    name: 'Oxygen saturation',
    chartName: 'SpO₂',
    includeInComparison: true,
    includeInAuto: true,
    startValue: 100,
    type: 'number',
    max: 100,
    min: 1,
    unit: '%',
    table: true,
    includeInManual: true,
  },
  ecg: {
    name: 'ECG Rhythm',
    chartName: 'ECG',
    startValue: 'NSR',
    type: 'string',
    fillFromPrevious: true,
    table: true,
  },
  rr: {
    name: 'Respiration Rate',
    chartName: 'RR',
    includeInComparison: true,
    includeInAuto: true,
    startValue: 16,
    type: 'number',
    unit: 'R/min',
    max: 200,
    min: 0,
    includeInManual: true,
  },
  temp: {
    name: 'Temperature',
    chartName: 'Temp',
    includeInAuto: true,
    startValue: opts.tempUnit === 'F' ? 98.7 : 37,
    type: 'float',
    step: 0.1,
    min: 0,
    max: opts.tempUnit === 'F' ? 122 : 50,
    unit: opts.tempUnit === 'F' ? '°F' : '°C',
    table: true,
  },
  fico2: {
    name: 'F.I. CO₂',
    chartName: 'FiCO₂',
    includeInAuto: true,
    startValue: 0,
    min: 0,
    max: 760,
    type: 'number',
    unit: 'mmHg',
    table: true,
  },
  etco2: {
    name: 'E.T. CO₂',
    chartName: 'EtCO₂',
    includeInComparison: true,
    includeInAuto: true,
    startValue: 40,
    min: 0,
    max: 1500,
    type: 'number',
    unit: 'mmHg',
    table: true,
    includeInManual: true,
  },
  fio2: {
    name: 'F.I. O₂',
    chartName: 'FiO₂',
    includeInAuto: true,
    startValue: 21,
    type: 'number',
    max: 1500,
    min: 0,
    // We don't specify a unit here because
    // it could be either mmHg or %
    unit: '',
    table: true,
  },
  eto2: {
    name: 'E.T. O₂',
    chartName: 'EtO₂',
    includeInAuto: true,
    startValue: 70,
    type: 'number',
    max: 1500,
    min: 0,
    // We don't specify a unit here because
    // it could be either mmHg or %
    unit: '',
    table: true,
  },
  fin2o: {
    name: 'F.I. N₂O',
    chartName: 'FiN₂O',
    includeInAuto: true,
    startValue: 50,
    type: 'number',
    max: 1500,
    min: 0,
    // We don't specify a unit here because
    // it could be either mmHg or %
    unit: '',
    table: true,
  },
  etn2o: {
    name: 'E.T. N₂O',
    chartName: 'EtN₂O',
    includeInAuto: true,
    startValue: 45,
    type: 'number',
    max: 1500,
    min: 0,
    // We don't specify a unit here because
    // it could be either mmHg or %
    unit: '',
    table: true,
  },
  sedLev: {
    name: 'Sedation Level',
    chartName: 'Sed Level',
    startValue: 'None',
    type: 'string',
    fillFromPrevious: true,
    table: true,
  },
  // Describes the patient's breathing status and degree of support, whether spontaneous, assisted, or fully controlled.
  ventStatus: {
    name: 'Ventilatory Status',
    chartName: 'Vent Status',
    startValue: 'Spon',
    type: 'string',
    fillFromPrevious: true,
    table: true,
  },
  // Specifies the operational mode of the ventilator when in use for mechanical ventilation.
  ventMode: {
    name: 'Ventilator Mode',
    chartName: 'Vent Mode',
    startValue: 'None',
    type: 'string',
    fillFromPrevious: true,
    table: true,
  },
  fisev: {
    name: 'F.I. Sevoflurane',
    chartName: 'FiSev',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  etsev: {
    name: 'E.T. Sevoflurane',
    chartName: 'EtSev',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  fides: {
    name: 'F.I. Desflurane',
    chartName: 'FiDes',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  etdes: {
    name: 'E.T. Desflurane',
    chartName: 'EtDes',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  fiiso: {
    name: 'F.I. Isoflurane',
    chartName: 'FiIso',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  etiso: {
    name: 'E.T. Isoflurane',
    chartName: 'EtIso',
    includeInAuto: true,
    startValue: 0,
    type: 'float',
    step: 0.01,
    min: 0,
    max: 100,
    unit: '%',
    table: true,
  },
  mac: {
    name: 'MAC',
    chartName: 'MAC',
    includeInAuto: true,
    type: 'float',
    startValue: 0.43,
    step: 0.01,
    min: 0,
    max: 130, // yes this is intentionally over 100%
    unit: '%',
    table: true,
  },
  peep: {
    name: 'PEEP',
    chartName: 'PEEP',
    step: 0.1,
    type: 'float',
    unit: 'cmH₂O',
    min: 0,
    max: 50,
    startValue: 4,
    table: true,
    fillFromPrevious: true,
  },
  pip: {
    name: 'PIP',
    chartName: 'PIP',
    step: 1,
    type: 'number',
    unit: 'cmH₂O',
    min: 0,
    max: 50,
    startValue: 20,
    table: true,
  },
  tv: {
    name: 'Tidal Volume',
    chartName: 'Tidal Volume',
    step: 1,
    type: 'number',
    unit: 'mL',
    min: 0,
    max: 10000,
    startValue: 500,
    table: true,
  },
  RSS: {
    name: 'Ramsay Sedation Scale',
    chartName: 'Sed. Level (RSS)',
    startValue: 2,
    type: 'manualNumber',
    min: 1,
    max: 6,
    table: true,
    fillFromPrevious: true,
  },
  GCS: {
    name: 'Glasgow Coma Scale',
    chartName: 'Sed. Level (GCS)',
    startValue: 15,
    type: 'manualNumber',
    min: 3,
    max: 15,
    table: true,
    fillFromPrevious: true,
  },
  ESS: {
    name: 'Ellis Sedation Score',
    chartName: 'Sed. Level (ESS)',
    startValue: 0,
    type: 'manualNumber',
    min: -5,
    max: 4,
    table: true,
    fillFromPrevious: true,
  },
})

/** @type {Record<string, number>} */
export const vitalIdSortOrderLookup = Object.keys(
  getVitalTypesById({ tempUnit: 'F' })
).reduce((acc, id, index) => {
  acc[id] = index
  return acc
}, {})

export const getVitalTypesBaseArray = shallowEqualMemoize(
  /**
   * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
   * @returns {(VitalTypeDefinition & {
   *   id: EveryVitalType
   * })[]}
   */ opts => {
    const base = getVitalTypesById(opts)
    const toReturn = []
    for (const id in base) {
      toReturn.push({ id, ...base[id] })
    }
    return toReturn
  }
)

export const getUserSelectableVitalTypes = shallowEqualMemoize(
  /**
   * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
   * @returns {UserSelectableVitalTypeWithId[]}
   */
  // @ts-ignore
  opts => getVitalTypesBaseArray(opts).filter(vital => !vital.subItem)
)

/** @returns {UserSelectableVitalTypeWithId[]} */
export const getManualVitalTypes = () =>
  // @ts-ignore
  // NB: we hardcode unit here because temp isn't included in the manual vitals list
  getVitalTypesBaseArray({ tempUnit: 'F' }).filter(
    vital => vital.includeInManual
  )

export const getVitalsNameAndUnitLookup = shallowEqualMemoize(
  /**
   * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
   * @returns {Record<EveryVitalType, [string, string?]>}
   */ opts => {
    const vitals = getVitalTypesBaseArray(opts)
    /** @type {Record<EveryVitalType, [string, string?]>} */
    // @ts-ignore
    const toReturn = {}
    for (const vital of vitals) {
      toReturn[vital.id] = [vital.chartName || vital.name, vital.unit]
    }
    return toReturn
  }
)

/** @type {EveryVitalType[]} */
export const vitalKeyTypesForComparison = getVitalTypesBaseArray({
  tempUnit: 'F',
})
  .filter(type => type.includeInComparison)
  .map(type => type.id)

/** @type {Record<string, string>} */
export const vitalsShortNameLookup = Object.entries(
  getVitalsNameAndUnitLookup({ tempUnit: 'F' })
).reduce((acc, [name, value]) => {
  acc[name] = value[0]
  return acc
}, {})
